import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { cloneDeep, isEqual } from 'lodash';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Observable, Subscription, forkJoin, map, of, switchMap } from 'rxjs';

import { KeyValueModel, KeyValueType } from '@models/common';
import { DisplayType, FieldOrigin, FieldTypeEnum, RestrictionType } from '@models/module/fields/enums';
import { AttachmentModel, FieldModel } from '@models/module/fields/main';
import { CipoControlComponent } from '@shared/components/fields/cipo-control/cipo-control.component';
import {
  CipoCheckboxControl,
  CipoControl,
  CipoFieldConfig,
  CipoFieldTypes,
  CipoListConfig,
  CipoListControl,
  CipoNumberControl,
  CipoTextControl,
} from '@shared/components/fields/common';
import { FROALA_TOOLBAR_SMALL } from '@shared/components/froala/common';
import { FroalaComponent } from '@shared/components/froala/froala.component';
import { CipoFroalaOptions } from '@shared/components/froala/interfaces';
import { LoadErrorComponent } from '@shared/components/load-error/load-error.component';
import { CipoDialogHeaderComponent, DirtyDialogBase } from '@shared/dialogs';
import { CipoAutofocusDirective } from '@shared/directives/cipo-autofocus.directive';
import { CipoDialogModule, CipoFieldsModule, CipoFormModule } from '@shared/modules';
import { NotificationService, UserService } from '@shared/services';
import { DictionaryStore } from '@signal-store';

import { FieldDataSource, ModuleConfigDictionariesService, NONE_OPTION_LABEL, applyServerErrors } from '../../common';
import {
  ALL_MIME_TYPES_KEY,
  FIELD_TYPES,
  FIELD_TYPE_LABELS,
  FORMATTINGS,
  FieldDefinitionsService,
  ListElementModel,
  RESTRICTIONS,
  ScreenFieldsService,
  displayTypeOf,
} from '../common';
import {
  ADDRESSES_DATA_SOURCE_ID,
  DATE_PICKER_TYPES,
  DEFAULT_FIELD_VERSION,
  FieldConfigDialogData,
  FieldConfigDialogResult,
  FieldConfigFormGroup,
  FormattingState,
  INTEGER_RESTRICTION_VALUE,
  RestrictionState,
  initFormattings,
  initRestrictions,
  requiredRestrictionMissing,
  restrictionValue,
  serializeFormattings,
  serializeRestrictions,
} from './field-config-dialog.models';
import { FormulaResult, aggregateLabel } from './field-formula/formula';
import { FieldFormulaViewsComponent } from './field-formula/formula-views/formula-views.component';
import { TableColumnRequest, TableColumnsComponent } from './table-columns/table-columns.component';

type DefaultValueKind =
  | 'none'
  | 'text'
  | 'number'
  | 'checkbox'
  | 'date'
  | 'richText'
  | 'richTextList'
  | 'list'
  | 'attachment';

const FORMULA_TYPES = [FieldTypeEnum.Formula, FieldTypeEnum.Number, FieldTypeEnum.Date];
const TABLE_TYPES = [FieldTypeEnum.Table, FieldTypeEnum.Weather];
const NO_DEFAULT_TYPES = [FieldTypeEnum.Table, FieldTypeEnum.Weather, FieldTypeEnum.Address];

/** Single-control defaults that fit next to the display type. */
const INLINE_DEFAULT_KINDS: DefaultValueKind[] = ['text', 'number', 'checkbox'];

@Component({
  selector: 'cipo-field-config-dialog',
  imports: [
    CipoAutofocusDirective,
    CipoControlComponent,
    CipoDialogHeaderComponent,
    CipoDialogModule,
    CipoFieldsModule,
    CipoFormModule,
    FieldFormulaViewsComponent,
    FlexLayoutModule,
    FroalaComponent,
    LoadErrorComponent,
    MatCheckboxModule,
    NgxSkeletonLoaderModule,
    TableColumnsComponent,
    TranslateModule,
  ],
  templateUrl: './field-config-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldConfigDialogComponent extends DirtyDialogBase {
  protected readonly data = inject<FieldConfigDialogData>(MAT_DIALOG_DATA);
  protected override readonly dialogRef = inject(MatDialogRef<FieldConfigDialogComponent, FieldConfigDialogResult>);
  private readonly fieldDefinitions = inject(FieldDefinitionsService);
  private readonly screenFields = inject(ScreenFieldsService);
  private readonly dictionaries = inject(ModuleConfigDictionariesService);
  private readonly dictionaryStore = inject(DictionaryStore);
  private readonly notify = inject(NotificationService);
  private readonly dialog = inject(MatDialog);
  private readonly userService = inject(UserService);

  private readonly formula = viewChild(FieldFormulaViewsComponent);
  private readonly tableColumns = viewChild(TableColumnsComponent);

  readonly loading = signal(true);
  /** The formula editor loads its fields on its own and holds no tokens until then: saving would clear the formula. */
  readonly formulaLoading = computed(() => !!this.formula()?.loading());
  readonly failed = signal(false);
  readonly unsupported = signal(false);
  readonly saving = signal(false);
  readonly loadingElements = signal(false);
  readonly tableEditing = signal(false);

  readonly fieldId = signal<number>(this.data.fieldId ?? null);
  readonly typeId = signal<FieldTypeEnum>(null);
  readonly displayTypeId = signal<DisplayType>(null);
  readonly multiple = signal(false);
  readonly screenId = signal<number>(this.data.screenId ?? 0);
  readonly fieldOrigin = signal<FieldOrigin>(this.data.fieldOrigin ?? FieldOrigin.Property);
  readonly inUse = signal(false);
  readonly canHaveAggregate = signal(false);
  readonly expression = signal<string>('');
  readonly addToForm = signal(!!this.data.addToForm);
  readonly restrictionStates = signal<RestrictionState[]>([]);
  readonly formattingStates = signal<FormattingState[]>([]);
  readonly fieldScreens = signal<KeyValueType<number, string>[]>([]);

  private readonly dataSources = signal<FieldDataSource[]>([]);
  private readonly parentSources = signal<FieldDataSource[]>([]);
  private readonly listElements = signal<ListElementModel[]>([]);
  private readonly aggregateFunctions = signal<KeyValueType<number, string>[]>([]);
  private readonly weatherDateFields = signal<KeyValueType<number, string>[]>([]);
  private readonly weatherLocationFields = signal<KeyValueType<number, string>[]>([]);
  private readonly mimeTypes = signal<KeyValueModel<number, string>[]>([]);
  private readonly selectedDataSourceId = signal<number>(null);
  private readonly anyChanges = signal(false);

  protected readonly isDirty = computed(() => this.anyChanges() || !!this.formula()?.changed());

  readonly form = this.buildForm();
  readonly fieldVersion = new CipoListControl(0, {
    type: CipoFieldTypes.Select,
    label: 'modules.fieldConfig.fieldVersion',
    hideNullOption: true,
    testId: 'module-config-field-dialog-field-version',
  });

  private entityIds: number[] = [];
  private details: Partial<FieldModel>;
  private editedLabel = false;
  private savedFormValue: unknown;
  private lastSaved: FieldConfigDialogResult;
  private restrictionSubscriptions = new Subscription();

  readonly fieldType = computed(() => FIELD_TYPES[this.typeId()]);
  readonly displayType = computed(() => displayTypeOf(this.typeId(), this.displayTypeId()));
  readonly hasDataSource = computed(() => !!this.displayType()?.hasDataSource);
  readonly isTableField = computed(() => !!this.data.tableId);
  readonly isEdit = computed(() => !!this.fieldId());
  readonly isDateType = computed(() => this.typeId() === FieldTypeEnum.Date);
  readonly lockedByScreen = computed(() => !!this.screenId() && !!this.fieldId());
  readonly showsTableSettings = computed(() => TABLE_TYPES.includes(this.typeId()) && !this.isTableField());

  readonly title = computed(() => (this.isEdit() ? 'modules.fieldConfig.editTitle' : 'modules.fieldConfig.addTitle'));
  readonly typeLabel = computed(() => FIELD_TYPE_LABELS[this.typeId()]);

  readonly displayBadge = computed(() => {
    const display = this.displayType();
    return display ? (this.multiple() ? display.labelMultiple : display.label) : undefined;
  });

  readonly detailBadges = computed(() => {
    const badges: string[] = [];
    this.formattingStates().forEach(state => {
      const element = FORMATTINGS[state.name].elements.find(entry => entry.key === state.value);
      if (element) {
        badges.push(element.label);
      }
    });
    this.restrictionStates().forEach(state => {
      const element = RESTRICTIONS[state.name].elements.find(entry => entry.key === state.value);
      if (element) {
        badges.push(element.label);
      }
    });
    return badges;
  });

  readonly canChooseMultiple = computed(() => !!this.fieldType()?.canHaveMultipleValues);
  readonly showsAddToForm = computed(() => this.data.canAddToForm && !this.inUse() && !this.isTableField());
  readonly showsFieldVersions = computed(() => !this.isTableField() && this.fieldScreens().length > 0);
  readonly showsFormula = computed(
    () => FORMULA_TYPES.includes(this.typeId()) && this.fieldOrigin() === FieldOrigin.Property,
  );
  readonly showsDataSource = computed(() => this.hasDataSource() && this.fieldOrigin() !== FieldOrigin.Relation);
  readonly showsWeather = computed(() => this.typeId() === FieldTypeEnum.Weather);
  readonly showsFilterSetting = computed(() => !this.screenId());

  readonly showsAggregate = computed(
    () =>
      this.defaultValueKind() !== 'none' &&
      !this.multiple() &&
      !this.hasDataSource() &&
      this.typeId() !== FieldTypeEnum.Attachment &&
      (this.canHaveAggregate() || (this.typeId() === FieldTypeEnum.Number && this.isTableField())),
  );

  readonly selectedDataSource = computed(() =>
    this.parentSources().find(source => source.key === this.selectedDataSourceId()),
  );
  readonly defaultOnlyOnScreen = computed(() => !!this.selectedDataSource()?.parentName && !this.screenId());

  readonly defaultValueKind = computed<DefaultValueKind>(() => {
    const typeId = this.typeId();
    const displayTypeId = this.displayTypeId();
    if (!typeId || !displayTypeId || NO_DEFAULT_TYPES.includes(typeId)) {
      return 'none';
    }
    if (this.hasDataSource()) {
      return this.fieldOrigin() === FieldOrigin.Relation ? 'none' : 'list';
    }
    if (typeId === FieldTypeEnum.Attachment) {
      return 'attachment';
    }
    if (typeId === FieldTypeEnum.YesNo) {
      return 'checkbox';
    }
    if (typeId === FieldTypeEnum.Number) {
      return 'number';
    }
    if (typeId === FieldTypeEnum.Date) {
      return 'date';
    }
    if (displayTypeId === DisplayType.RichText) {
      return this.multiple() ? 'richTextList' : 'richText';
    }
    if (typeId === FieldTypeEnum.Text) {
      return 'text';
    }
    return 'none';
  });

  readonly defaultValueConfig = computed<CipoFieldConfig>(() => {
    const kind = this.defaultValueKind();
    const multiple = this.multiple();
    const restrictions = this.restrictionStates();
    const base: CipoFieldConfig = {
      label: 'modules.fieldConfig.defaultValue',
      testId: 'module-config-field-dialog-field-default-value',
      multiple,
    };
    switch (kind) {
      case 'text':
        return { ...base, type: CipoFieldTypes.Text, multiline: this.displayTypeId() === DisplayType.MultiLine };
      case 'number': {
        const integer = restrictionValue(restrictions, 'Number') === INTEGER_RESTRICTION_VALUE;
        const decimals = this.formattingStates().find(state => state.name === 'Decimals')?.formattingValue;
        return { ...base, type: CipoFieldTypes.Number, decimals: integer ? 0 : (decimals ?? 2) };
      }
      case 'checkbox':
        return { ...base, type: CipoFieldTypes.Checkbox, label: 'modules.fieldConfig.selectedByDefault' };
      case 'date': {
        const pickerType = DATE_PICKER_TYPES[restrictionValue(restrictions, 'DateTime') as number];
        return { ...base, type: CipoFieldTypes.DateTime, multiple: false, pickerType: pickerType ?? 'date' };
      }
      case 'list':
        return {
          ...base,
          type: CipoFieldTypes.Select,
          options: this.listElements().map(element => ({ key: element.id, value: element.fullName ?? element.name })),
          readonly: false,
          noneLabel: NONE_OPTION_LABEL,
          hint: this.defaultOnlyOnScreen() ? 'modules.fieldConfig.defaultOnlyOnScreen' : undefined,
        } as CipoListConfig;
      case 'attachment':
        return { ...base, type: CipoFieldTypes.Attachment };
      default:
        return base;
    }
  });

  readonly inlinesDefaultValue = computed(
    () => INLINE_DEFAULT_KINDS.includes(this.defaultValueKind()) && !this.showsAggregate(),
  );

  readonly dataSourceFieldData = computed<CipoListConfig>(() => ({
    ...this.form.controls.dataSourceId.fieldData,
    options: this.dataSources().map(({ key, value }) => ({ key, value })),
    hint: this.lockedByScreen() ? 'modules.fieldConfig.dataSourceOnDefault' : undefined,
  }));

  readonly aggregateFieldData = computed<CipoListConfig>(() => ({
    ...this.form.controls.aggregateId.fieldData,
    options: this.aggregateFunctions().map(({ key }) => ({ key, value: aggregateLabel(key) ?? String(key) })),
  }));

  readonly displayTypeFieldData = computed<CipoListConfig>(() => ({
    ...this.form.controls.displayTypeId.fieldData,
    options: (this.fieldType()?.displayTypes ?? []).map(display => ({
      key: display.displayTypeId,
      value: this.multiple() ? display.labelMultiple : display.label,
    })),
  }));

  readonly weatherDateFieldData = computed<CipoListConfig>(() => ({
    ...this.form.controls.weatherDateId.fieldData,
    options: this.weatherDateFields(),
    hint: this.lockedByScreen() ? 'modules.fieldConfig.weatherDateOnDefault' : undefined,
  }));

  readonly weatherLocationFieldData = computed<CipoListConfig>(() => ({
    ...this.form.controls.weatherLocationId.fieldData,
    options: this.weatherLocationFields(),
    hint: this.lockedByScreen()
      ? 'modules.fieldConfig.weatherAddressOnDefault'
      : 'modules.fieldConfig.weatherAddressHint',
  }));

  readonly mimeTypeOptions = computed(() => [
    { key: ALL_MIME_TYPES_KEY, value: 'modules.fields.restrictions.allMimeTypes' },
    ...this.mimeTypes().map(type => ({
      key: type.key,
      value: type.description ? `${type.name ?? type.value} (${type.description})` : (type.name ?? type.value),
    })),
  ]);

  readonly fieldVersionFieldData = computed<CipoListConfig>(() => ({
    ...this.fieldVersion.fieldData,
    options: this.fieldScreens(),
  }));

  readonly mimeTypesFieldData = computed<CipoListConfig>(() => ({
    ...(this.form.controls.restrictions.controls['MimeTypes']?.fieldData as CipoListConfig),
    options: this.mimeTypeOptions(),
  }));

  readonly currentDateFieldData = computed<CipoFieldConfig>(() => {
    const dateTime = restrictionValue(this.restrictionStates(), 'DateTime');
    const label =
      dateTime === 4
        ? 'modules.fieldConfig.useCurrentDateTime'
        : dateTime === 5
          ? 'modules.fieldConfig.useCurrentTime'
          : 'modules.fieldConfig.useCurrentDate';
    return { ...this.form.controls.currentDateTime.fieldData, label };
  });

  readonly richTextOptions: CipoFroalaOptions = {
    ...FROALA_TOOLBAR_SMALL,
    heightMin: 0,
    heightMax: 400,
  };

  readonly richTextValues = computed(() => {
    this.anyChanges();
    const value = this.form.controls.defaultValue.value;
    return Array.isArray(value) && value.length ? (value as string[]) : [''];
  });

  constructor() {
    super();
    inject(DestroyRef).onDestroy(() => this.restrictionSubscriptions.unsubscribe());
    this.trackChanges();

    this.fieldVersion.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(screenId => this.changeFieldVersion(screenId as number));

    effect(() => {
      const blocked = this.defaultValueKind() === 'list' && (!this.listElements().length || this.defaultOnlyOnScreen());
      untracked(() => this.setEnabled(this.form.controls.defaultValue, !blocked));
    });

    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.failed.set(false);
    this.unsupported.set(false);

    forkJoin({
      mimeTypes: this.dictionaryStore.getMimeTypesDict(),
      parentSources: this.dictionaries.getDataSources(true),
      details: this.loadDetails(),
    }).subscribe({
      next: ({ mimeTypes, parentSources, details }) => {
        this.mimeTypes.set(mimeTypes ?? []);
        this.parentSources.set(parentSources);
        this.applyDetails(details.field, !!details.field.id);
        this.fieldScreens.set(details.screens);
        this.fieldVersion.setValue(this.screenId(), { emitEvent: false });
        this.setEnabled(this.fieldVersion, details.screens.length > 1);
        this.loading.set(false);
      },
      error: () => {
        this.failed.set(true);
        this.loading.set(false);
      },
    });
  }

  changeFieldVersion(screenId: number): void {
    if (screenId === this.screenId()) {
      return;
    }
    this.screenId.set(screenId ?? 0);
    this.load();
  }

  toggleAddToForm(value: boolean): void {
    this.addToForm.set(value);
    this.anyChanges.set(!isEqual(this.comparableValue(), this.savedFormValue));
  }

  // MARK: template helpers

  restrictionDefinition(state: RestrictionState) {
    return RESTRICTIONS[state.name];
  }

  restrictionControl(state: RestrictionState): CipoControl<unknown> {
    return this.form.controls.restrictions.controls[state.name];
  }

  showsRestrictionCheckbox(state: RestrictionState): boolean {
    return !this.isTableField() || state.key === RestrictionType.ShowThousandSeparator;
  }

  formattingControl(state: FormattingState): CipoControl<unknown> {
    return this.form.controls.formattings.controls[state.name];
  }

  // MARK: rich text defaults

  setRichText(value: string): void {
    this.form.controls.defaultValue.setValue(value);
  }

  setRichTextAt(index: number, value: string): void {
    const values = [...this.richTextValues()];
    values[index] = value;
    this.form.controls.defaultValue.setValue(values);
  }

  addRichTextValue(): void {
    const values = this.richTextValues();
    if (!values[values.length - 1]) {
      this.notify.warning('modules.fieldConfig.addValueFirst');
      return;
    }
    this.form.controls.defaultValue.setValue([...values, '']);
  }

  removeRichTextValue(index: number): void {
    const values = this.richTextValues().filter((_, position) => position !== index);
    this.form.controls.defaultValue.setValue(values.length ? values : ['']);
  }

  // MARK: table columns

  onTableEditing(editing: boolean): void {
    this.tableEditing.set(editing);
  }

  createColumn(request: TableColumnRequest): void {
    this.openColumnDialog({ newField: request, fieldOrigin: this.fieldOrigin() });
  }

  editColumn(fieldId: number): void {
    this.openColumnDialog({ fieldId });
  }

  // MARK: save

  save(): void {
    if (this.saving() || this.loading() || this.formulaLoading() || this.tableEditing()) {
      return;
    }
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    const restrictions = this.readRestrictions();
    const missingRestriction = requiredRestrictionMissing(restrictions);
    if (!this.displayTypeId()) {
      this.notify.error('modules.fieldConfig.selectDisplayType');
    }
    if (missingRestriction) {
      this.notify.error('modules.fieldConfig.selectRestrictions');
    }

    let formula: FormulaResult;
    if (this.showsFormula()) {
      formula = this.formula()?.result();
      if (!formula) {
        return;
      }
    }
    if (this.form.invalid || missingRestriction || !this.displayTypeId()) {
      return;
    }

    const body = this.buildBody(restrictions, formula);
    this.saving.set(true);
    this.request(body).subscribe({
      next: id => {
        const saved = { ...body, id: body.id ?? id };
        this.fieldId.set(saved.id);
        this.lastSaved = { field: saved, screenId: this.screenId(), addToForm: this.addToForm() };
        this.notify.success('modules.fieldConfig.savedSuccess');
        this.saving.set(false);
        this.setEnabled(this.form.controls.name, false);
        this.takeSnapshot();

        if (this.showsTableSettings()) {
          this.details = { ...this.details, ...saved };
          this.expression.set(saved.expression ?? '');
          return;
        }
        this.dialogRef.close(this.lastSaved);
      },
      error: (error: unknown) => {
        this.saving.set(false);
        applyServerErrors(this.form, error);
      },
    });
  }

  protected override getCloseResult(): unknown {
    return this.lastSaved;
  }

  // MARK: private: loading

  private loadDetails(): Observable<{ field: Partial<FieldModel>; screens: KeyValueType<number, string>[] }> {
    const { moduleId, newField, entityId, tableId } = this.data;
    const fieldId = this.fieldId();
    if (!fieldId && newField) {
      const field: Partial<FieldModel> = {
        typeId: newField.typeId,
        displayTypeId: newField.displayTypeId,
        fieldOrigin: this.fieldOrigin(),
        entityIds: entityId ? [entityId] : [],
        hasMultipleValues: newField.typeId === FieldTypeEnum.Attachment,
        restrictions: [],
        formattings: [],
      };
      return of({ field, screens: [] });
    }

    const screenId = this.screenId();
    const details$ = screenId
      ? this.screenFields.getFieldDetails(moduleId, fieldId, screenId)
      : this.fieldDefinitions.get(moduleId, fieldId);
    const screens$ = tableId
      ? of([])
      : this.screenFields.getFieldScreens(moduleId, fieldId).pipe(map(screens => [DEFAULT_FIELD_VERSION, ...screens]));
    return forkJoin({ field: details$, screens: screens$ });
  }

  private applyDetails(source: Partial<FieldModel>, isExisting: boolean): void {
    const type = FIELD_TYPES[source.typeId];
    if (!type) {
      this.typeId.set(source.typeId);
      this.unsupported.set(true);
      return;
    }

    const field = this.normalizeNumbers(source);
    this.details = field;
    this.entityIds = field.entityIds ?? [];
    this.editedLabel = isExisting;
    this.typeId.set(field.typeId);
    this.displayTypeId.set(field.displayTypeId);
    this.multiple.set(!!field.hasMultipleValues);
    this.fieldOrigin.set(field.fieldOrigin ?? this.fieldOrigin());
    this.inUse.set(!!field.inUse);
    this.canHaveAggregate.set(!!field.canHaveAggregate);
    this.expression.set(field.expression ?? '');
    this.selectedDataSourceId.set(field.dataSourceId ?? null);

    const restrictions = initRestrictions(type, field.restrictions, isExisting);
    const readOnly = restrictions.find(state => state.name === 'ReadOnly');
    const required = restrictions.find(state => state.name === 'Required');
    if (readOnly?.value && required) {
      required.value = false;
    }
    const display = displayTypeOf(field.typeId, field.displayTypeId);
    this.restrictionStates.set(restrictions);
    this.formattingStates.set(display ? initFormattings(display, field.formattings) : []);

    this.patchForm(field);
    this.loadDictionaries(field, isExisting);
    this.takeSnapshot();
  }

  private normalizeNumbers(field: Partial<FieldModel>): Partial<FieldModel> {
    if (field.typeId !== FieldTypeEnum.Number) {
      return field;
    }
    const toNumber = (value: unknown) =>
      value === null || value === undefined || value === '' ? value : parseFloat(value as string);
    return {
      ...field,
      defaultValue: toNumber(field.defaultValue),
      multipleDefaultValue: (field.multipleDefaultValue ?? []).map(toNumber),
    };
  }

  private loadDictionaries(field: Partial<FieldModel>, isExisting: boolean): void {
    const { moduleId } = this.data;
    this.loadDataSources();

    if (field.dataSourceId && field.typeId !== FieldTypeEnum.Relation) {
      this.loadElements(field.dataSourceId, true);
    }
    if (field.canHaveAggregate || (!isExisting && field.typeId === FieldTypeEnum.Number && !field.hasMultipleValues)) {
      this.dictionaries.getAggregateFunctions().subscribe(functions => this.aggregateFunctions.set(functions));
    }
    if (field.typeId === FieldTypeEnum.Weather) {
      forkJoin({
        dates: this.dictionaries.getModuleFields(moduleId, { type: FieldTypeEnum.Date, multiple: 0 }),
        locations: this.dictionaries.getModuleFields(moduleId, {
          type: FieldTypeEnum.Text,
          displayType: DisplayType.Select,
          dataSourceId: ADDRESSES_DATA_SOURCE_ID,
        }),
      }).subscribe(({ dates, locations }) => {
        this.weatherDateFields.set(dates);
        this.weatherLocationFields.set(locations);
      });
    }
  }

  private loadDataSources(): void {
    if (!this.hasDataSource()) {
      this.dataSources.set([]);
      return;
    }
    this.dictionaries
      .getDataSources(this.displayTypeId() === DisplayType.Select)
      .subscribe(sources => this.dataSources.set(sources));
  }

  private loadElements(dataSourceId: number, isInit: boolean): void {
    const source = this.parentSources().find(entry => entry.key === dataSourceId);
    const { moduleId } = this.data;
    this.listElements.set([]);

    if (!dataSourceId) {
      return;
    }
    if (source?.parentName && !this.screenId()) {
      this.clearDefaults();
      return;
    }

    const request$: Observable<ListElementModel[]> = source?.parentName
      ? this.screenFields.getParentDefaultValues(moduleId, source.parentId, this.screenId()).pipe(
          switchMap(parentElementIds =>
            this.fieldDefinitions.getListElements(moduleId, {
              id: dataSourceId,
              parentElementIds,
              parentListId: source.parentId,
            }),
          ),
        )
      : this.fieldDefinitions.getListElements(moduleId, { id: dataSourceId });

    this.loadingElements.set(true);
    request$.subscribe({
      next: elements => {
        this.listElements.set(elements);
        this.loadingElements.set(false);
        if (!isInit) {
          this.clearDefaults();
        }
      },
      error: () => this.loadingElements.set(false),
    });
  }

  // MARK: private: form

  private buildForm(): FormGroup<FieldConfigFormGroup> {
    const select = (label: string, testId: string, extra: Partial<CipoListConfig> = {}, required = false) =>
      new CipoListControl(
        null,
        { type: CipoFieldTypes.Select, label, testId, required, ...extra },
        required ? [Validators.required] : [],
      );
    const checkbox = (label: string, testId: string) =>
      new CipoCheckboxControl(false, { type: CipoFieldTypes.Checkbox, label, testId });

    return new FormGroup<FieldConfigFormGroup>({
      name: new CipoTextControl(
        '',
        {
          type: CipoFieldTypes.Text,
          label: 'modules.fieldConfig.name',
          required: true,
          testId: 'module-config-field-dialog-field-name',
        },
        [Validators.required],
      ),
      label: new CipoTextControl(
        '',
        {
          type: CipoFieldTypes.Text,
          label: 'modules.fieldConfig.label',
          required: true,
          testId: 'module-config-field-dialog-field-label',
        },
        [Validators.required],
      ),
      displayTypeId: select(
        'modules.fieldConfig.displayAs',
        'module-config-field-dialog-field-display-type',
        { hideNullOption: true },
        true,
      ),
      hasMultipleValues: checkbox('modules.fieldConfig.multipleValues', 'module-config-field-dialog-field-multiple'),
      aggregateId: select('modules.fieldConfig.aggregateFunction', 'module-config-field-dialog-field-aggregate', {
        noneLabel: NONE_OPTION_LABEL,
      }),
      dataSourceId: select('modules.fieldConfig.dataSource', 'module-config-field-dialog-field-data-source', {}, true),
      weatherDateId: select(
        'modules.fieldConfig.weatherDate',
        'module-config-field-dialog-field-weather-date',
        {},
        true,
      ),
      weatherLocationId: select(
        'modules.fieldConfig.weatherAddress',
        'module-config-field-dialog-field-weather-address',
        {},
        true,
      ),
      currentDateTime: checkbox('modules.fieldConfig.useCurrentDate', 'module-config-field-dialog-field-current-date'),
      isFilter: checkbox('modules.fieldConfig.alwaysOnFilters', 'module-config-field-dialog-field-filter'),
      defaultValue: new CipoControl<unknown>(null),
      restrictions: new FormGroup<Record<string, CipoControl<unknown>>>({}),
      formattings: new FormGroup<Record<string, CipoControl<unknown>>>({}),
    });
  }

  private patchForm(field: Partial<FieldModel>): void {
    const controls = this.form.controls;
    controls.name.setValue(field.name ?? '', { emitEvent: false });
    controls.label.setValue(field.label ?? '', { emitEvent: false });
    controls.displayTypeId.setValue(field.displayTypeId ?? null, { emitEvent: false });
    controls.hasMultipleValues.setValue(!!field.hasMultipleValues, { emitEvent: false });
    controls.aggregateId.setValue(field.aggregateId ?? null, { emitEvent: false });
    controls.dataSourceId.setValue(field.dataSourceId ?? null, { emitEvent: false });
    controls.weatherDateId.setValue(field.weatherDateId ?? null, { emitEvent: false });
    controls.weatherLocationId.setValue(field.weatherLocationId ?? null, { emitEvent: false });
    controls.currentDateTime.setValue(!!field.currentDateTime, { emitEvent: false });
    controls.isFilter.setValue(!!field.isFilter, { emitEvent: false });
    controls.defaultValue.setValue(this.initialDefaultValue(field), { emitEvent: false });

    this.setEnabled(controls.name, !field.id);
    this.setEnabled(controls.hasMultipleValues, this.canToggleMultiple(field));
    this.setEnabled(controls.displayTypeId, (this.fieldType()?.displayTypes.length ?? 0) > 1 || !field.displayTypeId);
    this.setEnabled(controls.aggregateId, !this.lockedByScreen());
    this.setEnabled(controls.dataSourceId, !this.lockedByScreen());
    this.setEnabled(controls.weatherDateId, !this.lockedByScreen());
    this.setEnabled(controls.weatherLocationId, !this.lockedByScreen());
    this.syncConditionalValidators();

    this.buildRestrictionControls();
    this.buildFormattingControls();
  }

  private initialDefaultValue(field: Partial<FieldModel>): unknown {
    const multiple = !!field.hasMultipleValues;
    if (field.typeId === FieldTypeEnum.Attachment) {
      return multiple ? (field.multipleDefaultValue ?? []) : ((field.defaultValue as AttachmentModel) ?? null);
    }
    if (field.typeId === FieldTypeEnum.YesNo) {
      return !!field.defaultValue;
    }
    if (multiple) {
      return (field.multipleDefaultValue ?? []).filter(value => value !== null && value !== undefined && value !== '');
    }
    return field.defaultValue ?? null;
  }

  private canToggleMultiple(field: Partial<FieldModel>): boolean {
    const origin = field.fieldOrigin ?? this.fieldOrigin();
    return (
      !(this.screenId() && origin === FieldOrigin.Property) && origin !== FieldOrigin.Relation && !!field.displayTypeId
    );
  }

  private buildRestrictionControls(): void {
    this.restrictionSubscriptions.unsubscribe();
    this.restrictionSubscriptions = new Subscription();
    const group = this.form.controls.restrictions;
    Object.keys(group.controls).forEach(name => group.removeControl(name, { emitEvent: false }));

    this.restrictionStates().forEach(state => {
      const definition = RESTRICTIONS[state.name];
      const testId = `module-config-field-dialog-restriction-${state.name.toLowerCase()}`;
      let control: CipoControl<unknown>;
      if (definition.isMimeTypes) {
        control = new CipoListControl(state.mimeTypes ?? [ALL_MIME_TYPES_KEY], {
          type: CipoFieldTypes.Select,
          label: definition.label,
          multiple: true,
          testId,
        });
        this.restrictionSubscriptions.add(
          control.valueChanges.subscribe(value => this.applyMimeToggle(control, value)),
        );
      } else if (definition.isNumberInput) {
        control = new CipoNumberControl(state.restrictionValue ?? null, {
          type: CipoFieldTypes.Number,
          label: definition.label,
          decimals: 0,
          min: 0,
          testId,
        });
      } else if (definition.elements.length) {
        control = new CipoListControl(
          state.value ?? null,
          {
            type: CipoFieldTypes.Select,
            label: definition.label,
            options: definition.elements.map(({ key, label }) => ({ key, value: label })),
            required: definition.required,
            hideNullOption: true,
            testId,
          },
          definition.required ? [Validators.required] : [],
        );
        this.restrictionSubscriptions.add(
          control.valueChanges.subscribe(value => this.onElementRestriction(state, value)),
        );
      } else {
        control = new CipoCheckboxControl(!!state.value, {
          type: CipoFieldTypes.Checkbox,
          label: definition.label,
          testId,
        });
        if (state.key === RestrictionType.ReadOnly) {
          this.restrictionSubscriptions.add(control.valueChanges.subscribe(value => this.onReadOnly(!!value)));
        }
      }
      group.addControl(state.name, control, { emitEvent: false });
    });

    const readOnly = group.controls['ReadOnly'];
    const required = group.controls['Required'];
    if (readOnly?.value && required) {
      required.disable({ emitEvent: false });
    }
  }

  private buildFormattingControls(): void {
    const group = this.form.controls.formattings;
    Object.keys(group.controls).forEach(name => group.removeControl(name, { emitEvent: false }));

    this.formattingStates().forEach(state => {
      const definition = FORMATTINGS[state.name];
      const testId = `module-config-field-dialog-formatting-${state.name.toLowerCase()}`;
      const control: CipoControl<unknown> = definition.elements.length
        ? new CipoListControl(state.value ?? null, {
            type: CipoFieldTypes.Select,
            label: definition.label,
            options: definition.elements.map(({ key, label }) => ({ key, value: label })),
            hideNullOption: true,
            testId,
          })
        : new CipoNumberControl(state.formattingValue ?? null, {
            type: CipoFieldTypes.Number,
            label: definition.label,
            decimals: 0,
            min: 0,
            testId,
          });
      group.addControl(state.name, control, { emitEvent: false });
    });
  }

  private trackChanges(): void {
    const controls = this.form.controls;

    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.savedFormValue !== undefined) {
        this.anyChanges.set(!isEqual(this.comparableValue(), this.savedFormValue));
      }
      this.restrictionStates.set(this.readRestrictions());
      this.formattingStates.set(this.readFormattings());
    });

    controls.name.valueChanges.pipe(takeUntilDestroyed()).subscribe(name => {
      if (!this.editedLabel || !controls.label.value) {
        controls.label.setValue(name as string, { emitEvent: false });
      }
    });

    controls.label.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => (this.editedLabel = true));

    controls.displayTypeId.valueChanges.pipe(takeUntilDestroyed()).subscribe(displayTypeId => {
      this.changeDisplayType(displayTypeId as DisplayType);
    });

    controls.hasMultipleValues.valueChanges.pipe(takeUntilDestroyed()).subscribe(multiple => {
      this.multiple.set(!!multiple);
      this.convertDefaultValue(!!multiple);
    });

    controls.dataSourceId.valueChanges.pipe(takeUntilDestroyed()).subscribe(dataSourceId => {
      this.selectedDataSourceId.set(dataSourceId as number);
      this.loadElements(dataSourceId as number, false);
    });

    controls.currentDateTime.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => {
      if (value) {
        controls.defaultValue.setValue(null, { emitEvent: false });
      }
    });

    controls.defaultValue.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => {
      if (this.defaultValueKind() === 'date' && value) {
        controls.currentDateTime.setValue(false, { emitEvent: false });
      }
    });
  }

  private changeDisplayType(displayTypeId: DisplayType): void {
    const previous = this.displayTypeId();
    if (!previous) {
      this.displayTypeId.set(displayTypeId);
      this.applyDetails({ ...this.details, ...this.valueForReload(), displayTypeId }, !!this.fieldId());
      return;
    }
    this.displayTypeId.set(displayTypeId);
    const source = this.selectedDataSource();
    if (source?.parentName && this.fieldOrigin() !== FieldOrigin.Relation) {
      this.form.controls.dataSourceId.setValue(null, { emitEvent: false });
      this.selectedDataSourceId.set(null);
      this.listElements.set([]);
    }
    this.syncConditionalValidators();
    this.loadDataSources();
    this.clearDefaults();
  }

  private valueForReload(): Partial<FieldModel> {
    const value = this.form.getRawValue();
    return { name: value.name as string, label: value.label as string };
  }

  private convertDefaultValue(multiple: boolean): void {
    const control = this.form.controls.defaultValue;
    const value = control.value;
    if (multiple) {
      const values = Array.isArray(value)
        ? value
        : value !== null && value !== undefined && value !== ''
          ? [value]
          : [];
      control.setValue(values, { emitEvent: false });
    } else {
      control.setValue(Array.isArray(value) ? (value[0] ?? null) : value, { emitEvent: false });
    }
    this.syncConditionalValidators();
  }

  private clearDefaults(): void {
    const multiple = this.multiple();
    const kind = this.defaultValueKind();
    const empty = kind === 'checkbox' ? false : multiple ? [] : null;
    this.form.controls.defaultValue.setValue(empty);
  }

  private onReadOnly(readOnly: boolean): void {
    const required = this.form.controls.restrictions.controls['Required'];
    if (!required) {
      return;
    }
    if (readOnly) {
      required.setValue(false, { emitEvent: false });
      required.disable({ emitEvent: false });
    } else {
      required.enable({ emitEvent: false });
    }
  }

  private onElementRestriction(state: RestrictionState, value: unknown): void {
    if (state.name === 'Number' && value === INTEGER_RESTRICTION_VALUE) {
      this.form.controls.formattings.controls['Decimals']?.setValue(0, { emitEvent: false });
    }
    this.clearDefaults();
  }

  private applyMimeToggle(control: CipoControl<unknown>, value: unknown): void {
    const selected = (value as number[]) ?? [];
    const previous = this.restrictionStates().find(state => state.name === 'MimeTypes')?.mimeTypes ?? [];
    const addedAll = selected.includes(ALL_MIME_TYPES_KEY) && !previous.includes(ALL_MIME_TYPES_KEY);
    if (addedAll) {
      control.setValue([ALL_MIME_TYPES_KEY], { emitEvent: false });
    } else if (selected.includes(ALL_MIME_TYPES_KEY) && selected.length > 1) {
      control.setValue(
        selected.filter(key => key !== ALL_MIME_TYPES_KEY),
        { emitEvent: false },
      );
    }
  }

  private syncConditionalValidators(): void {
    const controls = this.form.controls;
    const toggle = (control: CipoControl<unknown>, required: boolean) => {
      if (required) {
        control.addValidators(Validators.required);
      } else {
        control.removeValidators(Validators.required);
      }
      control.updateValueAndValidity({ emitEvent: false });
    };
    toggle(controls.dataSourceId, this.showsDataSource());
    toggle(controls.weatherDateId, this.showsWeather());
    toggle(controls.weatherLocationId, this.showsWeather());
  }

  private setEnabled(control: CipoControl<unknown>, enabled: boolean): void {
    if (enabled) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }

  private readRestrictions(): RestrictionState[] {
    const controls = this.form.controls.restrictions.controls;
    return this.restrictionStates().map(state => {
      const control = controls[state.name];
      if (!control) {
        return state;
      }
      const definition = RESTRICTIONS[state.name];
      if (definition.isMimeTypes) {
        return { ...state, mimeTypes: (control.value as number[]) ?? [] };
      }
      if (definition.isNumberInput) {
        return { ...state, restrictionValue: (control.value as number) ?? undefined };
      }
      if (definition.elements.length) {
        return { ...state, value: control.value as number };
      }
      return { ...state, value: !!control.value };
    });
  }

  private readFormattings(): FormattingState[] {
    const controls = this.form.controls.formattings.controls;
    return this.formattingStates().map(state => {
      const control = controls[state.name];
      if (!control) {
        return state;
      }
      return FORMATTINGS[state.name].elements.length
        ? { ...state, value: control.value as number }
        : { ...state, formattingValue: control.value as number };
    });
  }

  private comparableValue() {
    return { ...this.form.getRawValue(), addToForm: this.addToForm(), screenId: this.screenId() };
  }

  private takeSnapshot(): void {
    this.savedFormValue = cloneDeep(this.comparableValue());
    this.anyChanges.set(false);
  }

  // MARK: private: payload

  private buildBody(
    restrictions: RestrictionState[],
    formula: FormulaResult,
  ): Partial<FieldModel> & {
    screenId?: number;
  } {
    const value = this.form.getRawValue();
    const typeId = this.typeId();
    const multiple = !!value.hasMultipleValues;
    const kind = this.defaultValueKind();

    let defaultValue: unknown = null;
    let multipleDefaultValue: unknown[] = [];
    if (kind !== 'none') {
      if (multiple) {
        multipleDefaultValue = ((value.defaultValue as unknown[]) ?? []).filter(
          entry => entry !== null && entry !== '',
        );
      } else {
        defaultValue = value.defaultValue === '' ? null : (value.defaultValue ?? null);
      }
    }

    const usesFormula = FORMULA_TYPES.includes(typeId);
    return {
      id: this.fieldId() ?? undefined,
      name: value.name as string,
      label: value.label as string,
      typeId,
      displayTypeId: value.displayTypeId as DisplayType,
      hasMultipleValues: multiple,
      dataSourceId: (value.dataSourceId as number) ?? null,
      aggregateId: (value.aggregateId as number) ?? null,
      canHaveAggregate: this.canHaveAggregate(),
      currentDateTime: !!value.currentDateTime,
      isFilter: !!value.isFilter,
      useOnReport: !!this.details?.useOnReport,
      systemTableName: this.details?.systemTableName,
      systemFieldName: this.details?.systemFieldName,
      weatherDateId: (value.weatherDateId as number) ?? undefined,
      weatherLocationId: (value.weatherLocationId as number) ?? undefined,
      fieldOrigin: this.fieldOrigin(),
      inUse: this.inUse(),
      entityIds: this.entityIds,
      screenId: this.screenId(),
      expression: usesFormula ? (formula?.expression ?? null) : (this.details?.expression ?? ''),
      expressionFields: usesFormula ? (formula?.expressionFields ?? null) : this.details?.expressionFields,
      restrictions: serializeRestrictions(restrictions, typeId),
      formattings: serializeFormattings(this.readFormattings()),
      defaultValue,
      multipleDefaultValue,
    } as Partial<FieldModel> & { screenId?: number };
  }

  private request(body: Partial<FieldModel>): Observable<number> {
    const { moduleId, tableId } = this.data;
    const id = this.fieldId();
    const screenId = this.screenId();
    const done = (request$: Observable<unknown>) => request$.pipe(map(() => id));

    if (tableId) {
      return id
        ? done(this.fieldDefinitions.editTableColumn(moduleId, tableId, body))
        : this.fieldDefinitions.addTableColumn(moduleId, tableId, body);
    }
    if (!screenId) {
      return id ? done(this.fieldDefinitions.edit(moduleId, body)) : this.fieldDefinitions.add(moduleId, body);
    }
    return id
      ? done(this.screenFields.updateFieldDetails(moduleId, screenId, body))
      : this.fieldDefinitions.add(moduleId, body, screenId);
  }

  private openColumnDialog(options: Partial<FieldConfigDialogData>): void {
    this.dialog
      .open(FieldConfigDialogComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        ...this.userService.getResponsiveDialogSize().lg,
        data: {
          moduleId: this.data.moduleId,
          moduleTypeId: this.data.moduleTypeId,
          entityId: this.data.entityId,
          canAddToForm: false,
          tableId: this.fieldId(),
          ...options,
        } satisfies FieldConfigDialogData,
      })
      .afterClosed()
      .subscribe(() => this.tableColumns()?.reload());
  }
}
